import {
	formatNumber,
	type MatrixDefinition,
	type MatrixTable,
	type NumericCellValue,
	type RecordCellValue,
	type RecordDefinition,
	type RecordTableRow,
	type ResolvedColumnFormat,
	type WorkflowTableRenderResult,
} from "@/services/workflow-table/shared.js";
import type {
	WorkflowRenderedTableData,
	WorkflowTableBlock,
	WorkflowTableColumnFormat,
	WorkflowTableColumnFormatKind,
} from "@/types/memory.js";

export class WorkflowTableRenderer {
	renderMatrix(
		block: WorkflowTableBlock,
		definition: MatrixDefinition,
		matrix: MatrixTable,
		warnings: string[],
	): WorkflowTableRenderResult {
		return {
			content: this.renderMarkdownMatrix(definition, matrix),
			data: this.buildMatrixRenderedData(block, definition, matrix, warnings),
		};
	}

	renderRecords(
		block: WorkflowTableBlock,
		definition: RecordDefinition,
		rows: RecordTableRow[],
		totalRow: RecordTableRow | undefined,
		warnings: string[],
	): WorkflowTableRenderResult {
		return {
			content: this.renderMarkdownRecords(definition, rows, totalRow),
			data: this.buildRecordRenderedData(
				block,
				definition,
				rows,
				totalRow,
				warnings,
			),
		};
	}

	private buildMatrixRenderedData(
		block: WorkflowTableBlock,
		definition: MatrixDefinition,
		matrix: MatrixTable,
		warnings: string[],
	): WorkflowRenderedTableData {
		return {
			spec: {
				layout: "matrix",
				rowHeader: definition.rowHeader,
				rows: definition.visibleRows,
				columns: definition.visibleColumns,
				...(block.hiddenRows?.length ? { hiddenRows: block.hiddenRows } : {}),
				...(block.hiddenColumns?.length
					? { hiddenColumns: block.hiddenColumns }
					: {}),
				formulas: block.formulas,
				columnFormats: definition.columnFormats,
				rowFormats: definition.rowFormats,
			},
			...(block.unit ? { metadata: { unit: block.unit } } : {}),
			table: {
				headers: [definition.rowHeader, ...definition.visibleColumns],
				rows: definition.visibleRows.map((row) => ({
					key: row,
					kind: "data",
					cells: [
						row,
						...definition.visibleColumns.map(
							(column) => matrix[row]?.[column] ?? null,
						),
					],
				})),
			},
			warnings,
		};
	}

	private buildRecordRenderedData(
		block: WorkflowTableBlock,
		definition: RecordDefinition,
		rows: RecordTableRow[],
		totalRow: RecordTableRow | undefined,
		warnings: string[],
	): WorkflowRenderedTableData {
		return {
			spec: {
				layout: "records",
				columns: definition.visibleColumns,
				...(block.hiddenColumns?.length
					? { hiddenColumns: block.hiddenColumns }
					: {}),
				formulas: block.formulas,
				columnFormats: definition.columnFormats,
			},
			...(block.unit ? { metadata: { unit: block.unit } } : {}),
			table: {
				headers: definition.visibleColumns,
				rows: [
					...rows.map((row) => ({
						kind: "data" as const,
						cells: definition.visibleColumns.map(
							(column) => row[column] ?? null,
						),
					})),
					...(totalRow
						? [
								{
									kind: "total" as const,
									cells: definition.visibleColumns.map(
										(column) => totalRow[column] ?? null,
									),
								},
							]
						: []),
				],
			},
			warnings,
		};
	}

	private renderMarkdownMatrix(
		definition: MatrixDefinition,
		matrix: MatrixTable,
	): string {
		const lines = [
			`| ${[definition.rowHeader, ...definition.visibleColumns].join(" | ")} |`,
			`| ${["---", ...definition.visibleColumns.map(() => "---:")].join(" | ")} |`,
		];

		for (const row of definition.visibleRows) {
			const values = definition.visibleColumns.map((column) =>
				this.formatNumericCell(
					matrix[row]?.[column] ?? null,
					definition.rowFormats[row],
					definition.columnFormats[column],
					definition.percentRows.has(row) ||
						definition.percentColumns.has(column),
				),
			);
			lines.push(`| ${[row, ...values].join(" | ")} |`);
		}

		return `${lines.join("\n")}\n\n`;
	}

	private renderMarkdownRecords(
		definition: RecordDefinition,
		rows: RecordTableRow[],
		totalRow?: RecordTableRow,
	): string {
		const numericColumns = new Set(
			definition.visibleColumns.filter((column) => {
				if (definition.columnFormats[column]?.kind === "text") {
					return false;
				}
				return this.isNumericRecordColumn(column, rows, totalRow);
			}),
		);
		const lines = [
			`| ${definition.visibleColumns.join(" | ")} |`,
			`| ${definition.visibleColumns
				.map((column, index) =>
					index === 0 && !numericColumns.has(column) ? "---" : "---:",
				)
				.join(" | ")} |`,
		];

		for (const row of rows) {
			lines.push(
				`| ${definition.visibleColumns
					.map((column) =>
						this.formatRecordCell(
							row[column] ?? null,
							definition.columnFormats[column],
							definition.percentColumns.has(column),
						),
					)
					.join(" | ")} |`,
			);
		}

		if (totalRow) {
			lines.push(
				`| ${definition.visibleColumns
					.map(
						(column) =>
							`**${this.formatRecordCell(
								totalRow[column] ?? null,
								definition.columnFormats[column],
								definition.percentColumns.has(column),
							)}**`,
					)
					.join(" | ")} |`,
			);
		}

		return `${lines.join("\n")}\n\n`;
	}

	private formatNumericCell(
		value: NumericCellValue,
		rowFormat: WorkflowTableColumnFormat | undefined,
		columnFormat: WorkflowTableColumnFormat | undefined,
		isPercent: boolean,
	): string {
		return this.formatNumericValue(value, rowFormat, columnFormat, isPercent);
	}

	private formatRecordCell(
		value: RecordCellValue,
		columnFormat: WorkflowTableColumnFormat | undefined,
		isPercent: boolean,
	): string {
		if (value === null) {
			return this.resolveCellFormat(undefined, columnFormat, isPercent)
				.nullDisplay;
		}
		const resolvedFormat = this.resolveCellFormat(
			undefined,
			columnFormat,
			isPercent,
		);
		if (resolvedFormat.kind === "text") {
			return String(value);
		}
		if (typeof value === "number") {
			return this.formatNumericValue(value, undefined, columnFormat, isPercent);
		}
		return value;
	}

	private formatNumericValue(
		value: NumericCellValue,
		rowFormat: WorkflowTableColumnFormat | undefined,
		columnFormat: WorkflowTableColumnFormat | undefined,
		isPercent: boolean,
	): string {
		const resolvedFormat = this.resolveCellFormat(
			rowFormat,
			columnFormat,
			isPercent,
		);
		if (value === null) {
			return resolvedFormat.nullDisplay;
		}

		const formatted = formatNumber(value, {
			grouping: resolvedFormat.grouping,
			decimals: resolvedFormat.decimals,
		});
		return `${resolvedFormat.prefix}${formatted}${resolvedFormat.suffix}`;
	}

	private resolveCellFormat(
		rowFormat: WorkflowTableColumnFormat | undefined,
		columnFormat: WorkflowTableColumnFormat | undefined,
		isPercent: boolean,
	): ResolvedColumnFormat {
		const explicitKind =
			rowFormat?.kind && rowFormat.kind !== "auto"
				? rowFormat.kind
				: columnFormat?.kind && columnFormat.kind !== "auto"
					? columnFormat.kind
					: undefined;
		const kind = explicitKind ?? (isPercent ? "percent" : "number");

		const defaults: Record<
			Exclude<WorkflowTableColumnFormatKind, "auto">,
			Omit<ResolvedColumnFormat, "kind">
		> = {
			text: {
				grouping: false,
				decimals: 0,
				prefix: "",
				suffix: "",
				nullDisplay: "-",
			},
			number: {
				grouping: true,
				// undefined = keep the value's own decimal precision; an explicit
				// columnFormats.decimals still wins below.
				decimals: undefined,
				prefix: "",
				suffix: "",
				nullDisplay: "-",
			},
			currency: {
				grouping: true,
				decimals: 0,
				prefix: "",
				suffix: "",
				nullDisplay: "-",
			},
			percent: {
				grouping: false,
				decimals: 1,
				prefix: "",
				suffix: "%",
				nullDisplay: "-",
			},
		};

		const fallback = defaults[kind];
		return {
			kind,
			grouping:
				rowFormat?.grouping ?? columnFormat?.grouping ?? fallback.grouping,
			decimals:
				rowFormat?.decimals ?? columnFormat?.decimals ?? fallback.decimals,
			prefix: rowFormat?.prefix ?? columnFormat?.prefix ?? fallback.prefix,
			suffix: rowFormat?.suffix ?? columnFormat?.suffix ?? fallback.suffix,
			nullDisplay:
				rowFormat?.nullDisplay ??
				columnFormat?.nullDisplay ??
				fallback.nullDisplay,
		};
	}

	private isNumericRecordColumn(
		column: string,
		rows: RecordTableRow[],
		totalRow?: RecordTableRow,
	): boolean {
		const values = [
			...rows.map((row) => row[column]),
			totalRow ? totalRow[column] : undefined,
		].filter((value) => value !== undefined && value !== null);

		if (values.length === 0) {
			return false;
		}

		return values.every((value) => typeof value === "number");
	}
}
